import React, { ReactNode, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useConfirm } from 'material-ui-confirm';
import { Outlet, To } from 'react-router-dom';

import { CloudOff } from '@mui/icons-material';

import { Box, Divider, Typography } from '@mui/material';

import { useSelector } from '../../store/utils';

import { Header, HeaderProps } from './header';
import { NavSecondary } from './nav-secondary';
import { NavPrimary } from './nav-primary';
import { usePortal } from './portal';
import { IS_DEMO } from '../../config';
import { confirmYesNo } from '../../utils/use-confirm';
import { AppUpdateOverlay } from './app-update-overlay';
import { HeaderSubpage } from './header-subpage';

type Props = {
  title?: ReactNode;
  icon?: HeaderProps['icon'];
  variant?: 'default' | 'subpage';
  goBack?: To | -1;
};

export const MainLayout = ({
  title,
  icon,
  variant = 'default',
  goBack,
}: Props) => {
  const [desktopNavIsOpen, setDesktopNavIsOpen] = useState(false);

  const [connected, customer, pigpioInitError] = useSelector(
    ({ connected, variables: { CUSTOMER, PIGPIO_INIT_ERROR } }) =>
      [connected, CUSTOMER, PIGPIO_INIT_ERROR] as const
  );

  const { portalActive } = usePortal();
  useEffect(() => {
    if (IS_DEMO) {
      document.title = 'Infigy Demo | Tomáš Super';
    } else {
      document.title = portalActive ? `Infigy | ${customer}` : 'Infigy';
    }
  }, [portalActive, customer]);

  const confirm = useConfirm();

  useEffect(() => {
    if (pigpioInitError) {
      confirm({
        titleProps: { variant: 'h3' },
        title: (
          <FormattedMessage
            id="layout.pigpio_init_error_dialog.title"
            defaultMessage="Chyba při spuštění aplikace"
          />
        ),
        description: (
          <FormattedMessage
            id="layout.pigpio_init_error_dialog.description"
            defaultMessage="Aplikace Infigy na zařízení nenaběhla správně. Zkuste prosím zařízení restartovat a pokud problém přetrvává, kontaktujte podporu."
          />
        ),
        hideCancelButton: true,
        confirmationButtonProps: { variant: 'contained' },
        ...confirmYesNo,
      });
    }
  }, [pigpioInitError]);

  return (
    <Box
      component="main"
      sx={{ minHeight: '100vh', bgcolor: 'secondary.main', pt: 7 }}
    >
      <AppUpdateOverlay />
      {variant === 'default' ? (
        <Header
          title={
            title || (
              <FormattedMessage
                id="header.title"
                defaultMessage="Domovská stránka"
              />
            )
          }
          subtitle={
            <>
              {customer}, <FormattedDate value={new Date()} />
            </>
          }
          icon={icon}
          desktopNavToggle={() => setDesktopNavIsOpen(!desktopNavIsOpen)}
        />
      ) : (
        <HeaderSubpage
          title={title}
          goBack={goBack}
          desktopNavToggle={() => setDesktopNavIsOpen(!desktopNavIsOpen)}
        />
      )}

      {variant === 'default' ? (
        <NavSecondary sx={{ mb: 3 }} />
      ) : (
        <Divider sx={{ mb: 3 }} />
      )}

      <Outlet />

      {!connected && !IS_DEMO && (
        <Typography
          sx={{
            textAlign: 'center',
            py: { xs: 1, sm: 2 },
            pb: { sm: 3 },
            '& > *': { verticalAlign: 'middle' },
            opacity: 0.6,
          }}
          variant="body2"
        >
          <span>
            <FormattedMessage
              id="layout.disconnected"
              defaultMessage="Odpojeno"
            />
          </span>{' '}
          <CloudOff />
        </Typography>
      )}

      <NavPrimary
        desktopIsOpen={desktopNavIsOpen}
        desktopClose={() => setDesktopNavIsOpen(false)}
      />
    </Box>
  );
};
